import { Box, Typography, Paper } from "@mui/material";
import { useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";

import InviteAdminUserForm from "components/features/admin/InviteAdminUserForm";
import { useToast } from "contexts/ToastContext";
import { InviteAdminUserFormData } from "validations/adminSchemas";
import { EPath } from "constants/path";
import { adminUsersApi } from "apis";

function InviteAdminUserPage() {
  const { showSuccess, showError } = useToast();
  const navigate = useNavigate();

  const inviteAdminMutation = useMutation({
    mutationFn: (data: InviteAdminUserFormData) =>
      adminUsersApi.inviteAdminUser({
        inviteAdminUserRequestDto: data,
      }),
    onSuccess: () => {
      showSuccess(
        "User Invited Successfully",
        "The admin user has been invited and will receive an email with login credentials.",
      );
      navigate(EPath.HOME);
    },
    onError: (error: any) => {
      console.error("Error inviting admin user:", error);

      // Handle specific error cases
      if (error?.response?.status === 400) {
        if (error?.response?.data?.message?.includes("email")) {
          showError(
            "Email Already Exists",
            "This email has already been used. Please use a different email address.",
          );
        } else {
          showError("Invalid Input", "Please check your input and try again.");
        }
      } else {
        showError(
          "Invitation Failed",
          "An error occurred while inviting the user. Please try again.",
        );
      }
    },
  });

  const handleSubmit = (data: InviteAdminUserFormData) => {
    inviteAdminMutation.mutate(data);
  };

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", p: 3 }}>
      <Paper elevation={1} sx={{ p: 4, borderRadius: 2 }}>
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          sx={{ fontWeight: 600 }}
        >
          Invite New Admin User
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Enter the details below to invite a new admin user to the Cellera
          admin portal.
        </Typography>

        <InviteAdminUserForm
          onSubmit={handleSubmit}
          isLoading={inviteAdminMutation.isPending}
        />
      </Paper>
    </Box>
  );
}

export default InviteAdminUserPage;
